import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import type { LLMProviderSettings, LLMTestResult } from '../../services/types';
import { ProviderEditorModal } from '../../components/admin/ProviderEditorModal';

export function ProviderManagementTab() {
  const [providers, setProviders] = useState<LLMProviderSettings[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, LLMTestResult | { error: string }>>({});

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<LLMProviderSettings | null>(null);

  const loadProviders = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.getLLMProviders();
      setProviders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load providers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  const handleToggleEnabled = async (id: string, currentlyEnabled: boolean) => {
    const newEnabled = !currentlyEnabled;

    // Optimistic update
    setProviders(prev => prev.map(p =>
      p.id === id ? { ...p, is_enabled: newEnabled ? 1 : 0 } : p
    ));

    try {
      if (currentlyEnabled) {
        await api.disableLLMProvider(id);
      } else {
        await api.enableLLMProvider(id);
      }
    } catch (err) {
      // Rollback on error
      setProviders(prev => prev.map(p =>
        p.id === id ? { ...p, is_enabled: currentlyEnabled ? 1 : 0 } : p
      ));
      setError(err instanceof Error ? err.message : `Failed to ${currentlyEnabled ? 'disable' : 'enable'} provider`);
    }
  };

  const handleTest = async (id: string) => {
    try {
      setActionLoading(`test-${id}`);
      setTestResults(prev => ({ ...prev, [id]: undefined as unknown as LLMTestResult }));
      const result = await api.testLLMProvider(id);
      setTestResults(prev => ({ ...prev, [id]: result }));
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [id]: { error: err instanceof Error ? err.message : 'Test failed' }
      }));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: string, displayName: string) => {
    if (!confirm(`確定要刪除 "${displayName}" 嗎？`)) return;

    try {
      setActionLoading(id);
      await api.deleteLLMProvider(id);
      await loadProviders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete provider');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEdit = (provider: LLMProviderSettings) => {
    setEditingProvider(provider);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingProvider(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingProvider(null);
  };

  const handleModalSave = async () => {
    await loadProviders();
    handleModalClose();
  };

  const formatApiFormat = (format: string) => {
    switch (format) {
      case 'anthropic':
        return 'Anthropic Compatible';
      case 'ollama':
        return 'Ollama Compatible';
      default:
        return 'OpenAI Compatible';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Add Button */}
      <div className="flex justify-end mb-4">
        <button onClick={handleAdd} className="btn btn-primary btn-md">
          新增 Provider
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-4 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/50 text-danger-700 dark:text-danger-300 rounded-button">
          {error}
        </div>
      )}

      {/* Provider List */}
      <div className="space-y-4">
        {providers.length === 0 ? (
          <div className="card p-8 text-center">
            <p className="text-neutral-500 dark:text-neutral-400">
              尚未設定任何 Provider
            </p>
          </div>
        ) : (
          providers.map((provider) => {
            const testResult = testResults[provider.id];
            const isTestError = testResult && 'error' in testResult;

            return (
              <div
                key={provider.id}
                className={`card p-5 ${provider.is_enabled
                  ? 'ring-2 ring-primary-500 dark:ring-primary-400'
                  : 'opacity-75'
                }`}
              >
                <div className="flex items-start justify-between">
                  {/* Provider Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="heading-card text-neutral-900 dark:text-neutral-50">
                        {provider.display_name}
                      </h3>
                      {provider.is_enabled ? (
                        <span className="badge badge-success">啟用</span>
                      ) : (
                        <span className="badge badge-neutral">停用</span>
                      )}
                      <span className="badge badge-neutral">
                        {formatApiFormat(provider.api_format)}
                      </span>
                    </div>

                    <div className="mt-2 space-y-1 text-sm text-neutral-600 dark:text-neutral-400">
                      <p>
                        <span className="text-neutral-500">Provider ID:</span>{' '}
                        <code className="px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded text-xs">
                          {provider.provider_id}
                        </code>
                      </p>
                      <p>
                        <span className="text-neutral-500">Secret Name:</span>{' '}
                        <code className="px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded text-xs font-mono">
                          {provider.secret_name || '(未設定)'}
                        </code>
                      </p>
                      <p>
                        <span className="text-neutral-500">Base URL:</span> {provider.base_url}
                      </p>
                      <p>
                        <span className="text-neutral-500">Model:</span> {provider.default_model}
                      </p>
                      <p>
                        <span className="text-neutral-500">Priority:</span> {provider.priority}
                      </p>
                    </div>

                    {/* Test Result */}
                    {testResult && (
                      <div className={`mt-3 p-3 rounded-button text-sm ${isTestError
                        ? 'bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-300'
                        : 'bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300'
                      }`}>
                        {isTestError ? (
                          <p>測試失敗: {testResult.error}</p>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center gap-4">
                              <span>測試成功</span>
                              <span>延遲: {(testResult as LLMTestResult).latency_ms}ms</span>
                              <span>Tokens: {(testResult as LLMTestResult).usage.totalTokens}</span>
                            </div>
                            {(testResult as LLMTestResult).content && (
                              <div className="pt-2 border-t border-success-200 dark:border-success-800/50">
                                <p className="text-xs text-success-600 dark:text-success-400 mb-1">回應內容:</p>
                                <p className="font-mono text-xs bg-success-100 dark:bg-success-900/30 p-2 rounded">
                                  {(testResult as LLMTestResult).content}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center gap-2 mr-2">
                      <span className="text-xs text-neutral-500 dark:text-neutral-400">
                        {provider.is_enabled ? '啟用' : '停用'}
                      </span>
                      <button
                        onClick={() => handleToggleEnabled(provider.id, !!provider.is_enabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${provider.is_enabled
                          ? 'bg-primary-500'
                          : 'bg-neutral-300 dark:bg-neutral-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${provider.is_enabled ? 'translate-x-6' : 'translate-x-1'}`}
                        />
                      </button>
                    </div>

                    <button
                      onClick={() => handleTest(provider.id)}
                      disabled={actionLoading === `test-${provider.id}`}
                      className="btn btn-secondary btn-sm"
                    >
                      {actionLoading === `test-${provider.id}` ? (
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
                          測試中
                        </span>
                      ) : (
                        '測試'
                      )}
                    </button>

                    <button
                      onClick={() => handleEdit(provider)}
                      className="btn btn-ghost btn-sm"
                    >
                      編輯
                    </button>

                    {!provider.is_enabled && (
                      <button
                        onClick={() => handleDelete(provider.id, provider.display_name)}
                        disabled={actionLoading === provider.id}
                        className="btn btn-ghost btn-sm text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                      >
                        刪除
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Info Section */}
      <div className="mt-8 p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-card">
        <h4 className="heading-card text-neutral-900 dark:text-neutral-50 mb-2">
          API 格式說明
        </h4>
        <div className="text-sm text-neutral-600 dark:text-neutral-400 space-y-2">
          <p>
            <strong>Anthropic Compatible:</strong> 使用 <code>/messages</code> 端點，適用於 Kimi K2、MiniMax (Anthropic API)
          </p>
          <p>
            <strong>OpenAI Compatible:</strong> 使用 <code>/chat/completions</code> 端點，適用於 OpenRouter、MiniMax (OpenAI API)、本地 LLM
          </p>
          <p>
            <strong>Ollama Compatible:</strong> 使用 <code>/api/chat</code> 端點，適用於 Ollama Cloud 和本地 Ollama，支援動態載入模型列表
          </p>
        </div>
      </div>

      {/* Editor Modal */}
      <ProviderEditorModal
        isOpen={isModalOpen}
        provider={editingProvider}
        onClose={handleModalClose}
        onSave={handleModalSave}
      />
    </>
  );
}
